/* eslint-disable no-nested-ternary */
import React, { useImperativeHandle, forwardRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import { useForm } from 'react-hook-form';
import { Card, Typography, Button, Box, MenuItem, Select, CircularProgress } from '@mui/material';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import { isEmpty } from 'lodash';
import { generateReasonsLabel } from 'src/utils/getReferralData';
import { tableCellStyle, tableHeaderStyle } from 'src/styles/MetaSourceStyle';
import { setCurrentTreatmentDataGlobal } from 'src/redux/slices/multiTreatment';
import TreatmentSelectionAlert from 'src/components/alerts/TreatmentSelectionAlert';
import moment from 'moment';
import { useSelector, dispatch } from '../../../redux/store';
import { formatUkPhone } from '../../../utils/ukPhone';
import {
    selectFollowLead,
    selectTableRowLead,
} from '../../../redux/selectors/leadSelectors';
import { followUpAppointmentHandler, updateBookingSteps, clearLead, addScheduleReminder } from '../../../redux/slices/lead';
import { getCallScriptSection, setOpenCallScriptFromLeadPage } from '../../../redux/slices/callScript';
import { FormProvider } from '../../../components/hook-form';
import {
    OpenAppointmentNotBookedPopup,
    OpenAppointmentBookedPopup,
    CallScriptContainer
} from '../../../components/fullScreenPopUps';
import { useResponsive, useToggle } from '../../../hooks';
import { Iconify } from '../../../components';
import OutcomeCardGrid from './OutcomeCardGrid';
import { followUpBoxStyle, followUpTitleStyle, followUpSubTitleStyle, followUpCardStyle, numberTextStyle, textStyle } from '../../../styles/FollowUp';


function splitArrayAndCapitalize(arr, joinArray = true) {
    if (!joinArray) return arr
        .map(day => day.charAt(0) + day.slice(1).toLowerCase());
    return arr
        .map(day => day.charAt(0) + day.slice(1).toLowerCase()) // Capitalize first, rest to lower
        .join(", ");
}

function sortWeekdays(days) {
    if (!Array.isArray(days)) {
        // eslint-disable-next-line prefer-rest-params
        days = Array.from(arguments);
    }

    const dayOrder = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

    try {
        // Validate first
        // eslint-disable-next-line no-restricted-syntax
        for (const day of days) {
            if (!dayOrder.includes(day.toUpperCase())) {
                throw new Error('Invalid day in array');
            }
        }

        // Sort ignoring case, but return in original form
        return [...days].sort((a, b) =>
            dayOrder.indexOf(a.toUpperCase()) - dayOrder.indexOf(b.toUpperCase())
        );

    } catch (error) {
        console.warn('Unable to sort days, returning original array.', error);
        return days;
    }
}


const PREFERRED_CONTACT_METHOD = [
    { label: 'give me a call 📞', value: 'PHONE' },
    { label: 'send me a text 📱', value: 'TEXT' },
    { label: 'Whatsapp me 💬', value: 'WHATSAPP' },
    { label: 'drop me an email 📧', value: 'EMAIL' },
    { label: 'contact me', value: '' }
];

const PREFERRED_CONTACT_DAYS = [
    { label: "Monday", value: "MONDAY" },
    { label: "Tuesday", value: "TUESDAY" },
    { label: "Wednesday", value: "WEDNESDAY" },
    { label: "Thursday", value: "THURSDAY" },
    { label: "Friday", value: "FRIDAY" },
    { label: "Saturday", value: "SATURDAY" },
    { label: "Sunday", value: "SUNDAY" }
]

const CONTACT_PERIOD = [
    { label: "Morning (8AM - 12PM)", value: "MORNING" },
    { label: "Afternoon (12PM - 4PM)", value: "AFTERNOON" },
    { label: "Evening (4PM - 8PM)", value: "EVENING" },
]

const FollowUp = forwardRef(({ handleNext, handleClose, followLeadForScript, source = 'dashboard', hideNoAnswer = false, initialCard = null }, ref) => {
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const followLead = useSelector(selectFollowLead);
    const tableRowLead = useSelector(selectTableRowLead);
    const { callScriptData, openCallScriptFromLeadPage } = useSelector((state) => state.callScript);
    const { currentTreatmentDataGlobal } = useSelector((state) => state.multiTreatment);
    const [outcome, setOutcome] = useState(''); // eslint-disable-line no-unused-vars
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const isLight = theme.palette.mode === 'light';
    const [callScriptActive, setCallScriptActive] = useState(false);
    const metaData = followLead && followLead?.lead_metadata;
    const leadSourceMetaData = followLead && followLead?.lead_source && followLead?.lead_source?.metadata;
    const [leadId, setLeadId] = useState('');
    const [treatmentId, setTreatmentId] = useState('');
    const [metaDataInfo, setMetaDataInfo] = React.useState([]);
    const [contactInfo, setContactInfo] = React.useState([]);
    const [reminderObj, setReminderObject] = React.useState({});
    const [callScriptReloadPayLoad, setCallScriptReloadPayLoad] = useState({});
    // Detect WhatsApp lead from prop OR lead data in Redux
    const isWhatsAppLead = source === 'whatsapp'
        || followLead?.referral === 'WHATSAPP'
        || followLead?.lead_source?.referral === 'WHATSAPP'
        || followLead?.lead_contact?.preferred_contact_method?.toLowerCase() === 'whatsapp'
        || tableRowLead?.referral === 'WHATSAPP';
    const defaultValues = {
        state: 0
    };

    // Get treatment name for call script - computed directly to avoid dependency on getTreatmentName
    const treatmentNameForScript = !isEmpty(currentTreatmentDataGlobal?.treatmentData)
        ? currentTreatmentDataGlobal?.treatmentData?.treatment
        : (tableRowLead?.treatment_name || '');

    useEffect(() => {
        if (practiceId && treatmentNameForScript) {
            dispatch(getCallScriptSection({ practiceId, treatment: treatmentNameForScript, isTemplate: false, category: 'OUTBOUND' }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceId, treatmentNameForScript]);

    const methods = useForm({
        defaultValues,
    });

    const onSubmit = (data) => {
        if (data.state > 0) {
            handleNext(true);
        }
        if (data.state < 1) {
            enqueueSnackbar('Please click one of outcome', {
                variant: 'warning',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'center'
                }
            });
        }
    };

    const {
        handleSubmit,
        setValue,
    } = methods;

    useImperativeHandle(
        ref,
        () => ({
            async submit() {
                await handleSubmit(onSubmit)();
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const isMobile = useResponsive('up', 'sm'); // eslint-disable-line no-unused-vars

    const appoimentHandler = (data) => {
        setValue('state', 1);
        dispatch(followUpAppointmentHandler(data));
        setOutcome(data);
        dispatch(updateBookingSteps(data));
    };

    // The call flow's "No answer" for a lead: open straight on that outcome card, once.
    useEffect(() => {
        if (initialCard) appoimentHandler(initialCard);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Only open Call Scripts automatically when both the script list is ready
        // and we have a resolved followLead (from redux or the prop override).
        const readyLeadId = (followLeadForScript && followLeadForScript.id) || (followLead && followLead.id);
        if (openCallScriptFromLeadPage && callScriptData?.results?.length > 0 && readyLeadId) {
            setCallScriptActive(true);
        } else if (openCallScriptFromLeadPage && callScriptData !== null && Array.isArray(callScriptData?.results) && callScriptData.results.length === 0) {
            // Scripts fetched but none configured for this practice → the "Preparing call
            // script…" spinner would hang forever. Fall back to outcome cards so the user
            // can still log the call result.
            dispatch(setOpenCallScriptFromLeadPage(false));
        }
    }, [openCallScriptFromLeadPage, callScriptData, followLeadForScript, followLead]);

    const openCallScripts = () => {
        setCallScriptActive(true);
    };

    const {
        toggle: isAppointmentNotBooked,
        onOpen: onOpenAppointmentNotBooked,
        onClose: onCloseAppointmentNotBooked
    } = useToggle();

    const {
        toggle: isAppointmentBooked,
        onClose: onCloseAppointmentBooked
    } = useToggle();

    const onOpenAppointmentNotBookedEvent = (data) => {
        onOpenAppointmentNotBooked();
        setCallScriptReloadPayLoad(data);
    };

    const onOpenAppointmentBookedEvent = (data) => {
        // Close call script and trigger the main booking flow instead of opening separate popup
        setCallScriptActive(false);
        setCallScriptReloadPayLoad(data);
        // Trigger the same flow as clicking "Book an appointment" outcome button
        appoimentHandler('Booking Details');
    };

    const onOpenUnableToContactEvent = (data) => {
        // Close call script and trigger the Unable to Contact flow directly
        setCallScriptActive(false);
        setCallScriptReloadPayLoad(data);
        // Trigger the same flow as clicking "Unable to contact" outcome button
        appoimentHandler('Unable to contact');
    };

    const appointmentNotBookedClose = () => {
        onCloseAppointmentNotBooked();
        dispatch(clearLead());
        dispatch(setCurrentTreatmentDataGlobal(null));
        handleClose('finish', callScriptReloadPayLoad);
    };

    const onAppointmentNotBookClose = () => {
        onCloseAppointmentNotBooked();
    };

    const appointmentBookedClose = () => {
        onCloseAppointmentBooked();
        dispatch(clearLead());
        dispatch(setCurrentTreatmentDataGlobal(null));
        handleClose('finish', callScriptReloadPayLoad);
    };

    const onAppointmentBookClose = () => {
        onCloseAppointmentBooked();
    };

    const getTreatmentName = (name) => {
        if (!isEmpty(currentTreatmentDataGlobal?.treatmentData)) return currentTreatmentDataGlobal?.treatmentData?.treatment;
        return name;
    };

    // Check if treatment exists
    const hasTreatment = !isEmpty(currentTreatmentDataGlobal?.treatmentData) ||
        (followLead?.lead_treatments?.length > 0) ||
        (tableRowLead?.treatment_name);

    useEffect(() => {
        // setting info for metaData
        const leadMetaData = {};
        metaData?.forEach(dtObj => {
            const key = dtObj?.key ?? "";
            const value = dtObj?.value ?? "";
            if (key?.endsWith('?')) {
                leadMetaData[key] = value;
            }
        });
        Object.entries(leadSourceMetaData ?? {})?.forEach(([key, value]) => {
            if (key.endsWith('?')) {
                leadMetaData[key ?? ""] = value;
            }
        });
        const arrayInfo = Object.entries(leadMetaData ?? {})?.map(_ => _);
        setMetaDataInfo(arrayInfo);
    }, [metaData, leadSourceMetaData]);

    useEffect(() => {
        const leadMetaData = {};
        if (followLead?.lead_contact?.preferred_contact_days?.length) {
            leadMetaData.preferred_contact_days = sortWeekdays(splitArrayAndCapitalize(followLead?.lead_contact?.preferred_contact_days, false))
        }
        if (followLead?.lead_contact?.preferred_contact_periods?.length) {
            leadMetaData.preferred_contact_period = splitArrayAndCapitalize(followLead?.lead_contact?.preferred_contact_periods, false)
        }
        if (followLead?.lead_contact?.preferred_contact_method) {
            leadMetaData.preferred_contact_method = [followLead?.lead_contact?.preferred_contact_method]
        }
        setContactInfo(leadMetaData);
        setReminderObject({
            preferredMethod: leadMetaData.preferred_contact_method?.[0],
            prefferedDay: leadMetaData.preferred_contact_days?.[0],
            preferredTime: leadMetaData.preferred_contact_period?.[0],
            assignedUser: followLead?.assignees?.[0]?.id
        })
    }, [followLead]);

    const updateReminderObject = (isMethod, isDay, isPeriod) => {
        setReminderObject(prv => {
            if (isMethod) return { ...prv, preferredMethod: isMethod }
            if (isDay) return { ...prv, prefferedDay: isDay }
            if (isPeriod) return { ...prv, preferredTime: isPeriod }
            return prv
        })
    }

    function getNextWeekday(dayOfWeek) {
        // Normalize first
        dayOfWeek = dayOfWeek?.toLowerCase() ?? "monday";

        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const today = moment();
        const todayIndex = today.day();

        const targetIndex = days.indexOf(dayOfWeek);
        if (targetIndex === -1) {
            // fallback to next Monday if invalid
            return today.add(7 - todayIndex + 1, 'days');
        }

        let daysToAdd = (targetIndex - todayIndex + 7) % 7;
        if (daysToAdd === 0) daysToAdd = 7; // If it's the same day, move it forward a week

        return today.add(daysToAdd, 'days');
    }

    function setTimeBlock(date, block) {
        // Normalize first
        block = block?.toLowerCase() ?? "morning";

        if (block.startsWith('morning')) {
            return date.hour(8).minute(0).second(0);
        } if (block.startsWith('afternoon')) {
            return date.hour(12).minute(0).second(0);
        } if (block.startsWith('evening')) {
            return date.hour(16).minute(0).second(0);
        }
        // fallback to 9 AM if not recognized
        return date.hour(9).minute(0).second(0);
    }

    const sendReminder = ({ preferredMethod, prefferedDay, preferredTime, assignedUser }) => {
        const datetime = setTimeBlock(getNextWeekday(prefferedDay), preferredTime);
        let method = "";
        const methodInput = preferredMethod?.toLowerCase();

        if (methodInput === "phone" || methodInput === "call")
            method = "CALL";
        else if (methodInput === "email" || methodInput === "email")
            method = "EMAIL";
        else if (methodInput === "sms" || methodInput === "text")
            method = "SMS";
        else if (methodInput === "whatsapp")
            method = "WHATSAPP";
        else
            method = "OTHER"

        const addReminderObj = {
            method,
            datetime: `${datetime.format('DD-MM-YYYY HH:mm:ss')}.0Z`,
            note: `Please ${preferredMethod?.toLowerCase() || "contact"} ${followLead?.salutation} ${followLead?.first_name} ${followLead?.last_name} in the ${preferredTime?.toLowerCase() || ""}`,
            is_completed: false,
            assignees: assignedUser ? [assignedUser] : [],
            group: 'FOLLOW_UP',
            lead: followLead?.id,
            lead_treatment: followLead?.lead_treatments?.[0]?.id
        };

        dispatch(addScheduleReminder(addReminderObj, practiceId, ({ message, success }) => {
            if (success) {
                // Setting a reminder IS a valid outcome — mark state so Finish
                // proceeds. Without this, leads with no checklist task left state=0
                // and Finish kept warning "Please click one of outcome".
                setValue('state', 1);
                setOutcome(addReminderObj);
            }
            enqueueSnackbar(success ? `Reminder has been set for ${datetime.format('dddd, Do MMMM YYYY [at] hA')}` : message, {
                variant: success ? 'success' : 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'center'
                }
            })
        }));
    }

    return (
        <Box sx={followUpBoxStyle}>
            {/* Journey stepper moved to green header bar in OpenContactLeadPopup */}

            {/* Content area - three rows: top preferences, center heading, bottom phone+outcomes */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>

            {/* Treatment selection alert - shown at very top when no treatment */}
            {!hasTreatment && (
                <Box sx={{ width: '100%', pt: 2, px: 2 }}>
                    <TreatmentSelectionAlert
                        leadId={followLead?.id}
                        leadData={followLead}
                    />
                </Box>
            )}

            {/* Top — preferences */}
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', pt: hasTreatment ? 1.5 : 1 }}>
            {Object.entries(contactInfo ?? {})?.length && contactInfo?.preferred_contact_method?.[0]?.length && contactInfo?.preferred_contact_method?.[0] !== "NO_PREFERENCE" ? (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        // Constrain to the container width so the row WRAPS instead of
                        // growing past the popup and pushing the "Set" button off-screen.
                        width: '100%',
                        maxWidth: '100%',
                    }}
                >
                    <Typography
                        variant="caption"
                        color="text.disabled"
                        fontWeight={500}
                        sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}
                    >
                        {followLead?.first_name || 'Patient'} prefers
                    </Typography>

                    <Select
                        variant="outlined"
                        size="small"
                        defaultValue={contactInfo?.preferred_contact_method?.[0] || ""}
                        onChange={e => updateReminderObject(e?.target?.value, false, false)}
                        sx={{
                            minWidth: '120px',
                            borderRadius: '10px',
                            height: '28px',
                            background: 'transparent',
                            boxShadow: 'none',
                            border: '1px solid rgba(145, 158, 171, 0.16)',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: 'text.secondary',
                            '&:hover': { borderColor: 'rgba(145, 158, 171, 0.3)' },
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            '& .MuiSelect-select': { padding: '4px 8px' }
                        }}
                    >
                        {contactInfo?.preferred_contact_method?.map((item) => {
                            const label = PREFERRED_CONTACT_METHOD?.filter(e => e?.value?.toLowerCase() === item?.toLowerCase())?.[0]?.label || "contact me";
                            return (
                                <MenuItem key={item} value={item}>
                                    {label}
                                </MenuItem>
                            )
                        })}
                    </Select>

                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '12px' }}>on</Typography>

                    <Select
                        variant="outlined"
                        size="small"
                        defaultValue={contactInfo?.preferred_contact_days?.[0] || "MONDAY"}
                        onChange={e => updateReminderObject(false, e?.target?.value, false)}
                        sx={{
                            minWidth: '90px',
                            height: '28px',
                            borderRadius: '10px',
                            background: 'transparent',
                            boxShadow: 'none',
                            border: '1px solid rgba(145, 158, 171, 0.16)',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: 'text.secondary',
                            '&:hover': { borderColor: 'rgba(145, 158, 171, 0.3)' },
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            '& .MuiSelect-select': { padding: '4px 8px' }
                        }}
                    >
                        {contactInfo?.preferred_contact_days?.map((item) => {
                            const label = PREFERRED_CONTACT_DAYS?.filter(e => e?.value?.toLowerCase() === item?.toLowerCase())?.[0]?.label || "";
                            return (
                                <MenuItem key={item} value={item}>
                                    {label}
                                </MenuItem>
                            )
                        }) ?? <MenuItem value={"MONDAY"}>
                                Monday
                            </MenuItem>}
                    </Select>

                    <Select
                        variant="outlined"
                        size="small"
                        defaultValue={contactInfo?.preferred_contact_period?.[0] || "MORNING"}
                        onChange={e => updateReminderObject(false, false, e?.target?.value)}
                        sx={{
                            minWidth: '90px',
                            height: '28px',
                            borderRadius: '10px',
                            background: 'transparent',
                            boxShadow: 'none',
                            border: '1px solid rgba(145, 158, 171, 0.16)',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: 'text.secondary',
                            '&:hover': { borderColor: 'rgba(145, 158, 171, 0.3)' },
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            '& .MuiSelect-select': { padding: '4px 8px' }
                        }}
                    >
                        {contactInfo?.preferred_contact_period?.map((item) => {
                            const label = CONTACT_PERIOD?.filter(e => e?.value?.toLowerCase() === item?.toLowerCase())?.[0]?.label || "";
                            return (
                                <MenuItem key={item} value={item}>
                                    {label}
                                </MenuItem>
                            )
                        }) ?? <MenuItem value={"MORNING"}>
                                Morning (8AM - 12PM)
                            </MenuItem>}
                    </Select>

                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disableElevation
                        sx={{
                            borderRadius: '10px',
                            textTransform: 'none',
                            height: '28px',
                            px: 2,
                            fontSize: '12px',
                            fontWeight: 600,
                            minWidth: 'auto',
                            // Never let the button shrink/clip away when the row wraps.
                            flexShrink: 0,
                        }}
                        onClick={() => sendReminder(reminderObj)}
                    >
                        Set reminder
                    </Button>
                </Box>
            ) : null}
            </Box>

            {/* Middle */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                <FormProvider methods={methods}>
                    <Typography
                        sx={{ ...followUpTitleStyle, fontSize: '16px', fontWeight: 400, color: '#637381' }}
                    >
                        {isWhatsAppLead ? 'Send WhatsApp to' : 'Call'} <span style={{ textTransform: 'capitalize', fontWeight: 700, color: '#212B36' }}>{followLead && followLead.first_name || ''} {followLead && followLead.last_name || ''}</span> {isWhatsAppLead ? 'about' : 'to book an appointment for'}
                    </Typography>
                    {getTreatmentName(tableRowLead && tableRowLead.treatment_name) ? (
                        <Typography
                            sx={{ ...followUpTitleStyle, fontSize: '20px', fontWeight: 700, color: '#212B36', mt: 0.5 }}
                        >
                            {getTreatmentName(tableRowLead.treatment_name)}
                        </Typography>
                    ) : null}
                    {!isEmpty(followLead?.lead_contact?.note) && <Typography
                        variant="p"
                        sx={followUpSubTitleStyle}
                    >
                        {`"${followLead.lead_contact.note}"`}
                    </Typography>}
                </FormProvider>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, mb: 0.5 }}>
                    {!isEmpty(followLead?.lead_contact?.phone) && (
                        <Iconify
                            icon={'solar:copy-linear'}
                            width={16}
                            height={16}
                            style={{ color: '#919EAB', cursor: 'pointer', marginRight: 8, flexShrink: 0 }}
                            onClick={() => {
                                navigator.clipboard.writeText(followLead.lead_contact.phone);
                                enqueueSnackbar('Phone number copied', { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
                            }}
                        />
                    )}
                    <a
                        href={`tel: ${followLead && followLead.lead_contact && followLead.lead_contact.phone}`}
                        style={{ textDecoration: 'none' }}
                    >
                        {!isEmpty(followLead?.lead_contact?.phone) && <Typography
                            color={isLight ? 'grey.800' : '#fff'}
                            sx={{
                                ...numberTextStyle,
                                fontSize: '18px', fontWeight: 600, letterSpacing: '0.5px',
                            }}
                        >
                            {formatUkPhone(followLead.lead_contact.phone)}
                        </Typography>}
                    </a>
                </Box>
                {/* Call script prompt — available for all leads */}
                {!callScriptActive && callScriptData?.results?.length > 0 && (
                    <Box onClick={() => openCallScripts()}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5, mt: 3, px: 2.5, py: 1.5,
                            borderRadius: '12px', cursor: 'pointer', width: '100%', maxWidth: 460,
                            background: 'linear-gradient(135deg, rgba(7,141,238,0.04) 0%, rgba(0,171,85,0.04) 100%)',
                            border: '1px solid rgba(7,141,238,0.12)',
                            transition: 'all 200ms ease',
                            '&:hover': { background: 'linear-gradient(135deg, rgba(7,141,238,0.08) 0%, rgba(0,171,85,0.08) 100%)', borderColor: 'rgba(7,141,238,0.24)', transform: 'translateY(-1px)', boxShadow: '0 2px 8px rgba(7,141,238,0.08)' },
                        }}>
                        <Box sx={{ width: 36, height: 36, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', flexShrink: 0 }}>
                            <Iconify icon="eva:file-text-fill" width={18} sx={{ color: '#078DEE' }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#212B36', lineHeight: 1.3 }}>Open Call Script</Typography>
                            <Typography sx={{ fontSize: '11px', color: '#919EAB', lineHeight: 1.3 }}>Guided conversation for {getTreatmentName(tableRowLead?.treatment_name) || 'this treatment'}</Typography>
                        </Box>
                        <Iconify icon="eva:arrow-forward-fill" width={16} sx={{ color: '#078DEE', flexShrink: 0 }} />
                    </Box>
                )}
            </Box>

            {/* Bottom */}
            {/* {((metaData && metaData.length !== 0 && metaData.some(el => el.key && el.key.endsWith('?'))) || (leadSourceMetaData && Object.entries(leadSourceMetaData)?.length !== 0 && Object.entries(leadSourceMetaData).some(([key]) => key && key.endsWith('?')))) && <Card sx={{ mb: 3, p: '12px 24px' }}>
                <List sx={{ maxWidth: '100%', m: '0 0 -24px', p: 0 }}>
                    {metaData && metaData?.map((el, index) => {
                        const { key, value } = el;
                        if (key.endsWith('?')) {
                            return (
                                <ListItem alignItems="flex-start" key={index} sx={{ p: '8px 0' }}>
                                    <ListItemAvatar sx={{ mt: '1px' }}>
                                        <Iconify icon={'charm:info'} color={'#1890FF'} width={24} height={24} />
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={key}
                                        secondary={`— ${value}`}
                                        sx={{ ...textStyle, textAlign: 'left' }}
                                    />
                                </ListItem>
                            );
                        }
                        return null;
                    })}
                    {leadSourceMetaData && Object.entries(leadSourceMetaData).map(([key, value], index) => {
                        if (key.endsWith('?')) {
                            return (
                                <ListItem alignItems="flex-start" key={index} sx={{ p: '8px 0' }}>
                                    <ListItemAvatar sx={{ mt: '1px' }}>
                                        <Iconify icon={'charm:info'} color={'#3366FF'} width={24} height={24} />
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={key}
                                        secondary={`— ${value}`}
                                        sx={{ ...textStyle, textAlign: 'left' }}
                                    />
                                </ListItem>
                            );
                        }
                        return null;
                    })}
                </List>
            </Card>} */}
            <Card sx={{ ...followUpCardStyle, width: callScriptActive ? '100%' : 'auto', mb: '80px' }} >
                {!callScriptActive ? (
                    openCallScriptFromLeadPage ? (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            minHeight: 260,
                            py: 4
                        }}>
                            <CircularProgress color="primary" sx={{ mb: 2 }} />
                            <Typography variant='body2' color='text.secondary'>Preparing call script…</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', width: '100%', pb: 2 }}>

                    {metaDataInfo?.length !== 0 && <Card sx={{ width: "100%", mb: 3 }}>
                        <Box sx={{ display: 'flex' }}>
                            <Typography sx={tableHeaderStyle(isLight)}>Lead Data</Typography>
                            <Typography sx={tableHeaderStyle(isLight)}>Value</Typography>
                        </Box>
                        {metaDataInfo?.map(([key, value], index) => (
                            <Box sx={{ display: 'flex' }} key={`${key}-${index}`}>
                                <Typography sx={tableCellStyle}>{generateReasonsLabel(key ?? "")}</Typography>
                                <Typography sx={tableCellStyle}>{generateReasonsLabel(value ?? "")}</Typography>
                            </Box>
                        ))}
                    </Card>}

                    {/* Outcome cards */}
                    <Typography variant="body2" sx={{ ...textStyle, fontWeight: 500, color: '#919EAB', mb: { xs: '8px', sm: '16px' }, mt: { xs: '4px', sm: '8px' }, fontSize: { xs: '10px', sm: '11px' }, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Outcome</Typography>
                    <OutcomeCardGrid
                        onSelect={appoimentHandler}
                        options={(isWhatsAppLead ? [
                            { key: 'Booking Details', icon: 'eva:calendar-fill', label: 'Book Appointment', sub: 'Patient wants to book', color: '#00AB55', bg: 'rgba(0,171,85,0.06)' },
                            { key: 'WhatsApp Follow Up', icon: 'eva:clock-fill', label: 'Follow up', sub: 'Set a reminder', color: '#078DEE', bg: 'rgba(7,141,238,0.06)' },
                            { key: 'WhatsApp Close', icon: 'eva:close-circle-fill', label: 'Close', sub: 'Close conversation', color: '#637381', bg: 'rgba(145,158,171,0.06)' },
                        ] : [
                            { key: 'Booking Details', icon: 'eva:calendar-fill', label: 'Book Appointment', sub: 'Patient wants to book', color: '#00AB55', bg: 'rgba(0,171,85,0.06)' },
                            { key: 'Appointment Not Booked', icon: 'eva:message-circle-fill', label: 'Spoke - Not Booked', sub: 'Spoke but didn\'t book', color: '#FFAB00', bg: 'rgba(255,171,0,0.06)' },
                            { key: 'Unable to contact', icon: 'eva:close-circle-fill', label: 'No Answer', sub: 'No answer, voicemail, or wrong number', color: '#FF5630', bg: 'rgba(255,86,48,0.06)' },
                        ]).filter((card) => !(hideNoAnswer && card.key === 'Unable to contact'))}
                        columns={hideNoAnswer ? 2 : 3}
                    />

                    {/* Non-contact options are in the footer (OpenContactLeadPopup) */}
                </Box>
                    )
                ) : null}
            </Card>
            </Box>
            {callScriptActive && (
                <CallScriptContainer
                    open={callScriptActive}
                    onClose={(reason, reloadPayload) => {
                        setCallScriptActive(false);
                        if (reason === 'finish') {
                            handleClose(reason, reloadPayload);
                        }
                    }}
                    scriptList={callScriptData?.results}
                    followLead={followLeadForScript || followLead}
                    onOpenAppointmentNotBooked={onOpenAppointmentNotBookedEvent}
                    onOpenAppointmentBooked={onOpenAppointmentBookedEvent}
                    onOpenUnableToContact={onOpenUnableToContactEvent}
                    setLeadId={setLeadId}
                    setTreatmentId={setTreatmentId}
                    useV2
                    callType="outbound"
                />
            )}
            {isAppointmentNotBooked && <OpenAppointmentNotBookedPopup
                open={isAppointmentNotBooked}
                headerButtons
                onClose={appointmentNotBookedClose}
                leadId={leadId}
                onAppointmentNotBookClose={onAppointmentNotBookClose}
            />}
            {isAppointmentBooked && <OpenAppointmentBookedPopup
                open={isAppointmentBooked}
                headerButtons
                onClose={appointmentBookedClose}
                leadId={leadId}
                treatmentId={treatmentId}
                onAppointmentBookClose={onAppointmentBookClose}
            />}
        </Box>
    );
});

FollowUp.propTypes = {
    handleNext: PropTypes.func,
    handleClose: PropTypes.func,
    followLeadForScript: PropTypes.any,
    source: PropTypes.string,
    // The call being logged ended answered: leave out the No Answer card (call flow).
    hideNoAnswer: PropTypes.bool,
    // Select this outcome card as soon as the step opens (the call flow's "No answer" for a lead).
    initialCard: PropTypes.string,
};

export default FollowUp;
